import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faRotateRight, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import { formatCuit } from "../rolloFormat";
import { SEARCH_MIN_LENGTH, buildClienteResumen, cleanClienteText, plural } from "./nuevaGestionModel";

const SKELETON_WIDTHS = [
  ["38%", "78%"],
  ["52%", "64%"],
  ["30%", "82%"],
  ["44%", "70%"],
];

export const clienteNombre = (cliente) => buildClienteResumen(cliente).nombre || "Sin nombre";

function Note({ error = false, icon, title, children }) {
  return (
    <div className={`crm-rollo__note${error ? " crm-rollo__note--error" : ""}`} role={error ? "alert" : undefined}>
      <span className="crm-rollo__note-ico" aria-hidden="true">
        <FontAwesomeIcon icon={icon} />
      </span>
      <b>{title}</b>
      {children}
    </div>
  );
}

function ClienteCard({ cliente, selected, onSelect, onConfirm }) {
  const razonSocial = cleanClienteText(cliente.razonSocial);
  const cuit = cleanClienteText(cliente.cuit);
  const terminales = Number(cliente.cantidadTerminales) || 0;
  const comercios = Number(cliente.cantidadComercios) || 0;

  return (
    <label className={`crm-rollo__rcard${selected ? " is-selected" : ""}`} onDoubleClick={() => onConfirm(cliente.groupKey)}>
      <input
        type="radio"
        name="crm-rollo-cliente"
        value={cliente.groupKey}
        checked={selected}
        onChange={() => onSelect(cliente.groupKey)}
      />
      <div>
        <div className="crm-rollo__rcard-name">{clienteNombre(cliente)}</div>
        <div className="crm-rollo__rcard-meta">
          {razonSocial ? <span>{razonSocial}</span> : null}
          {cuit ? <span>{`CUIT ${formatCuit(cuit)}`}</span> : null}
          {terminales ? <span>{plural(terminales, "terminal", "terminales")}</span> : null}
          {comercios ? <span>{plural(comercios, "comercio", "comercios")}</span> : null}
        </div>
      </div>
    </label>
  );
}

/**
 * Step 1 · Seleccionar cliente (design RolloNewSelect). Controlled by NuevaGestionModal, which owns the search
 * so the query, results and selection survive "Cambiar cliente". Results are RadioCards grouped by client.
 */
export default function ClienteStep({ inputRef, query, onQueryChange, search, selectedKey, onSelect, onConfirm, onRetry }) {
  const renderResults = () => {
    if (search.status === "idle") {
      return (
        <Note icon={faMagnifyingGlass} title="Buscá el cliente que pidió los rollos">
          <p>{`Escribí al menos ${SEARCH_MIN_LENGTH} caracteres del nombre, CUIT, terminal, comercio, razón social o legajo.`}</p>
        </Note>
      );
    }

    if (search.status === "loading") {
      return (
        <div className="crm-rollo__rcards" aria-busy="true" aria-label="Buscando clientes">
          {SKELETON_WIDTHS.map(([title, meta]) => (
            <div key={title} className="crm-rollo__rcard" aria-hidden="true">
              <span className="crm-rollo__skel crm-rollo__skel--radio" />
              <div className="crm-rollo__rcard-skel">
                <span className="crm-rollo__skel crm-rollo__skel--tall" style={{ width: title }} />
                <span className="crm-rollo__skel" style={{ width: meta }} />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (search.status === "error") {
      return (
        <Note error icon={faTriangleExclamation} title="No pudimos buscar clientes">
          <p>Hubo un problema de conexión con el servidor. Probá de nuevo en unos segundos.</p>
          <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" onClick={onRetry}>
            <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
            Reintentar
          </button>
        </Note>
      );
    }

    if (!search.results.length) {
      return (
        <Note icon={faMagnifyingGlass} title={`No encontramos clientes para “${search.text}”`}>
          <p>Probá con el CUIT sin guiones, el número de terminal, el número de comercio o el legajo.</p>
        </Note>
      );
    }

    return (
      <>
        <div className="crm-rollo__list-meta">{plural(search.results.length, "resultado", "resultados")}</div>
        <fieldset className="crm-rollo__rcards" role="radiogroup" aria-label="Clientes">
          <legend className="crm-rollo__sr-only">Clientes</legend>
          {search.results.map((cliente) => (
            <ClienteCard
              key={cliente.groupKey}
              cliente={cliente}
              selected={selectedKey === cliente.groupKey}
              onSelect={onSelect}
              onConfirm={onConfirm}
            />
          ))}
        </fieldset>
      </>
    );
  };

  return (
    <>
      <label className="crm-rollo__field crm-rollo__search crm-rollo__modal-search">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="crm-rollo__search-ico" aria-hidden="true" />
        <input
          ref={inputRef}
          className="crm-rollo__input"
          type="search"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Buscar por nombre, CUIT, terminal, comercio, razón social, legajo..."
          aria-label="Buscar cliente"
          autoComplete="off"
        />
      </label>
      <div className="crm-rollo__picker" aria-live="polite">
        {renderResults()}
      </div>
    </>
  );
}
